import { z } from 'zod';
import { ShareRole } from './enums';

/**
 * Standard error envelope used by EVERY non-2xx API response (api-contract §0.5).
 * The UI keys off `error.code` (machine string), never the human `message`.
 */
export const ErrorCode = z.enum([
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'INTERNAL',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ErrorResponse = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().nullable().default(null),
  }),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

/** GET /api/health (api-contract §1.1) — the only public endpoint. */
export const HealthResponse = z.object({
  status: z.literal('ok'),
  uptime: z.number(),
  timestamp: z.string().datetime(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

/** GET /api/me (api-contract §1.2) — mirrored local users row, public projection. */
export const Me = z.object({
  id: z.string().uuid(),
  clerkUserId: z.string(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  createdAt: z.string().datetime(),
  // Admin gate for the web client (the admin email list stays server-side).
  isAdmin: z.boolean(),
  // True when an admin has blocked this user — the client shows a blocked screen.
  blocked: z.boolean(),
});
export type Me = z.infer<typeof Me>;

/** Lightweight public user identity embedded in collaborative resources. */
export const UserSummary = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().nullable(),
});
export type UserSummary = z.infer<typeof UserSummary>;

/**
 * Per-list sharing role (PRD §12). Declared now even though shares land in T29,
 * so the role string is stable across the codebase (api-contract §6).
 */
export const MasterListShareRole = ShareRole;
export type MasterListShareRole = z.infer<typeof MasterListShareRole>;
